<template>
  <div id="wrapper">
    <nav class="navbar is-dark">
      <!-- BRAND -->
      <div class="navbar-brand">
        <router-link to="/" class="navbar-item">
          <strong>My Tiny Inc</strong>
        </router-link>

        <!-- BURGER (mobile only) -->
        <a
          role="button"
          class="navbar-burger"
          :class="{ 'is-active': isMenuOpen }"
          aria-label="menu"
          aria-expanded="false"
          @click="isMenuOpen = !isMenuOpen"
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </a>
      </div>

      <!-- MENU -->
      <div class="navbar-menu" :class="{ 'is-active': isMenuOpen }">
        <div class="navbar-end" v-if="isLogged">
          <router-link to="/dashboard" class="navbar-item"
            >Dashboard</router-link
          >
          <router-link to="/dashboard/third-parties" class="navbar-item"
            >Thirdparties</router-link
          >
          <router-link to="/dashboard/documents" class="navbar-item"
            >Documents</router-link
          >
          <router-link to="/dashboard/inventory" class="navbar-item"
            >Inventory</router-link
          >
          <router-link to="/analytics" class="navbar-item"
            >Analytics</router-link
          >

          <!-- PEPPOL -->
          <div
            class="navbar-item has-dropdown"
            :class="{
              'is-hoverable': !isMobile,
              'is-active': isMobile && isPeppolOpen,
            }"
          >
            <a class="navbar-link" @click.prevent="isMobile && togglePeppol()">
              Peppol
            </a>

            <div class="navbar-dropdown">
              <router-link
                v-if="peppolActive"
                to="/organization/peppol"
                class="navbar-item"
                @click="closeAllMenus"
              >
                Company
              </router-link>

              <router-link
                to="/organization/peppol-verify"
                class="navbar-item"
                @click="closeAllMenus"
              >
                Verify
              </router-link>

              <router-link
                to="/organization/peppol-documents"
                class="navbar-item"
                @click="closeAllMenus"
              >
                Documents
              </router-link>

              <router-link
                to="/organization/peppol-inbox"
                class="navbar-item"
                @click="closeAllMenus"
              >
                Inbox
              </router-link>
            </div>
          </div>

          <!-- ADD DOCUMENT -->
          <div class="navbar-item">
            <router-link
              to="/dashboard/documents/add"
              class="button is-success"
            >
              Add Document
            </router-link>
          </div>

          <!-- USER MENU -->
          <div
            class="navbar-item has-dropdown"
            :class="{
              'is-hoverable': !isMobile,
              'is-active': isMobile && isUserMenuOpen,
            }"
          >
            <a
              class="navbar-link"
              @click.prevent="isMobile && toggleUserMenu()"
            >
              <figure class="image is-32x32 mr-2">
                <img
                  class="is-rounded"
                  src="https://bulma.io/images/placeholders/64x64.png"
                />
              </figure>
              {{ displayName }}
            </a>

            <div class="navbar-dropdown is-right">
              <router-link
                to="/dashboard/my-account"
                class="navbar-item"
                @click="closeAllMenus"
              >
                My Account
              </router-link>

              <router-link
                to="/dashboard/preferences"
                class="navbar-item"
                @click="closeAllMenus"
              >
                Preferences
              </router-link>

              <router-link
                v-if="canManage"
                to="/settings/manage"
                class="navbar-item"
                @click="closeAllMenus"
              >
                Manage
              </router-link>
              <router-link
                v-if="canManage"
                to="/settings/activity-log"
                class="navbar-item"
                @click="closeAllMenus"
              >
                Activity Log
              </router-link>
              <!--
              <router-link
                v-if="canManage"
                to="/settings/import"
                class="navbar-item"
                @click="closeAllMenus"
              >
                Import Data
              </router-link>
              -->
              <hr class="navbar-divider" />

              <a class="navbar-item" @click.prevent="onLogout"> Logout </a>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <section class="section">
      <!-- Global page loader -->
      <GlobalLoader v-if="loading" />

      <router-view />
    </section>

    <footer class="footer">
      <p class="has-text-centered">Copyright (c) 2025</p>
    </footer>
  </div>
</template>
<script>
// English-only comments

import GlobalLoader from './components/GlobalLoader.vue';

export default {
  name: 'App',
  components: {
    GlobalLoader,
  },
  data() {
    return {
      isMenuOpen: false,
      isPeppolOpen: false,
      isUserMenuOpen: false,
      isMobile: false,
    };
  },
  watch: {
    loading(val) {
      console.log('LOADING STATE:', val);
    },
  },
  computed: {
    loading() {
      return this.$store.state.loading;
    },
    isLogged() {
      return this.$store.getters.isLogged;
    },
    displayName() {
      const u = this.$store.getters.currentUser || {};
      return (
        [u.first_name, u.last_name].filter(Boolean).join(' ') ||
        u.email ||
        'User'
      );
    },
    currentUser() {
      return this.$store.getters.currentUser || {};
    },
    rawMemberships() {
      return this.currentUser.memberships || [];
    },
    activeOrganizationId() {
      return this.$store.state?.activeOrganizationId ?? null;
    },
    canManage() {
      const u = this.currentUser;

      // Global privileges
      if (u.is_superuser || u.is_staff || u.is_owner) {
        return true;
      }

      const memberships = this.rawMemberships || [];

      // Try to match active organization if available
      let membership = null;

      if (this.activeOrganizationId) {
        membership = memberships.find(
          (mm) =>
            String(mm.organization?.id ?? mm.organization) ===
            String(this.activeOrganizationId)
        );
      }

      // Fallback: any owner/admin membership
      if (!membership) {
        membership = memberships.find((mm) =>
          ['owner', 'admin'].includes((mm.role || '').toLowerCase())
        );
      }

      return ['owner', 'admin'].includes(
        (membership?.role || '').toLowerCase()
      );
    },
    peppolActive() {
      return this.$store.state.peppolActive;
    },
  },
  mounted() {
    this.updateIsMobile();
    window.addEventListener('resize', this.updateIsMobile);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateIsMobile);
  },
  methods: {
    updateIsMobile() {
      this.isMobile = window.innerWidth < 1024; // Bulma desktop breakpoint
    },
    togglePeppol() {
      this.isPeppolOpen = !this.isPeppolOpen;
      this.isUserMenuOpen = false;
    },
    toggleUserMenu() {
      this.isUserMenuOpen = !this.isUserMenuOpen;
      this.isPeppolOpen = false;
    },
    closeAllMenus() {
      this.isMenuOpen = false;
      this.isPeppolOpen = false;
      this.isUserMenuOpen = false;
    },
    async onLogout() {
      try {
        await this.$store.dispatch('logOut');
      } finally {
        this.closeAllMenus();
        this.$router.replace('/log-in');
      }
    },
  },
};
</script>
